import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, Node } from '@contentful/rich-text-types';
import Product from './Product';
import './Page.scss';
import { useContentful } from '../contentful/ContentContext';
import NewsletterSignup from './Newsletter';
import ContentBlock from './ContentBlock';

const Page = ({
  pageId,
  standalone = false,
}: {
  pageId: string;
  standalone?: boolean;
}) => {
  const { getPageById, getProduct, getUiText } = useContentful();
  const page = getPageById(pageId);

  if (!page) return <div>Page not found</div>;

  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
        const { file, title, description } = node.data.target.fields;
        const tags = node.data.target.metadata.tags;
        // @ts-expect-error
        const isInlineSmall = tags?.some((tag) => {
          return tag?.sys?.id === 'inlineSmall';
        });

        // @ts-expect-error
        const isSlinganLink = tags?.some((tag) => {
          return tag?.sys?.id === 'slinganLink';
        });

        const slinganLinkUrl = getUiText('UI.SlinganLinkURL');

        const Tag = isSlinganLink ? 'a' : 'div';

        return (
          <Tag
            href={isSlinganLink ? (slinganLinkUrl ?? undefined) : undefined}
            className={
              'embeddedAssetWrapper' + (isInlineSmall ? ' inlineSmall' : '')
            }>
            <img
              src={`https:${file.url}`}
              className={'embeddedAsset'}
              alt={description || title}
            />
            {description && <p className={'imageDescription'}>{description}</p>}
          </Tag>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
        const entry = node.data.target;
        const entryType = entry.sys.contentType.sys.id;

        switch (entryType) {
          case 'contentBlock':
            return <ContentBlock fields={entry.fields} />;
          case 'imageRow':
            const images = entry.fields.images;
            return (
              <div className="imageRow">
                {images.map((image: any) => {
                  const { file, title, description } = image.fields;
                  return (
                    <div key={image.sys.id} className={'imageWrapper'}>
                      <img
                        src={`https:${file.url}`}
                        alt={description || title}
                        className={'imageRowImage'}
                      />
                      {description && (
                        <p className={'imageDescription'}>{description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          default:
            return <div>Unsupported embedded content type: {entryType}</div>;
        }
      },
      [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
        const entry = node.data.target;
        const entryType = entry.sys.contentType.sys.id;

        switch (entryType) {
          case 'product':
            const product = getProduct(entry.fields.id);
            // @ts-expect-error
            return product ? <Product product={product.fields} /> : null;

          case 'heroImage':
            const { file, title, description } = entry.fields.image.fields;
            return (
              <span className={'heroImageWrapper'}>
                <img
                  className={'heroImage'}
                  src={`https:${file.url}`}
                  height="100%"
                  width="100%"
                  alt={description || title}
                />
              </span>
            );

          case 'testimonial':
            const quote = entry.fields.quote;

            return (
              <div className="testimonial">
                <p className="quote">{quote}</p>
              </div>
            );

          case 'newslettersignup':
            return <NewsletterSignup customText={entry?.fields?.customText} />;

          default:
            return <span>Unsupported inline content type: {entryType}</span>;
        }
      },
    },
  };

  const pageDocument = documentToReactComponents(
    page.fields.pageContent as Document,
    renderOptions
  );
  const products = Array.isArray(page.fields?.products)
    ? page.fields.products
    : null;

  const heroEntry = page.fields.heroImage;
  const heroAsset = heroEntry?.fields?.image?.fields;

  return (
    <div
      className={[
        'pageWrapper',
        standalone && 'pageWrapper--standalone',
        'pageWrapper--withFooterSpacing',
      ]
        .filter(Boolean)
        .join(' ')}>
      {heroAsset && (
        <span className={'heroImageWrapper'}>
          <img
            className={'heroImage'}
            src={`https:${heroAsset.file.url}`}
            width="100%"
            height="100%"
            alt={heroAsset.description || heroAsset.title || ''}
          />
        </span>
      )}
      <div className={'pageContent'}>{pageDocument}</div>
      {products && (
        <div className={'productGrid'}>
          {products?.map((product: any) => (
            <Product key={product?.sys.id} product={product.fields} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Page;
